import {Injectable} from '@angular/core';
import {EUStates, StatusColor} from '@app/models/abstract-contact.model';
import {ContactModel} from '@app/models/contact.model';
import {ServiceNumberModel} from '@app/models/service-number.model';
import {GtAbsenceService} from '@app/services/gt-absence.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtManageOrganizationService} from '@app/services/gt-manage-organization.service';
import {PushEventService} from '@app/services/push-event/push-event.service';
import {AgentStateTransition, EUCallEvent, EUEvent, SkillEvent} from '@app/services/push-event/push-events';
import {checkResponse} from '@app/shared/utils/app-utils';
import {
  ComponentFavourite,
  Contact,
  ContactFavourite,
  ContactImage,
  Department,
  DirectoryService,
  DirectoryV2Service,
  EndUser,
  EndUserAccess,
  EndUserNumber,
  Favourite,
  Organization,
  ServiceNumber,
  SipTerminal,
  Trunk
} from '@gt-api';
import {forkJoin, merge, Observable, of, ReplaySubject} from 'rxjs';
import {catchError, distinctUntilChanged, filter, first, map, mapTo, switchMap, tap} from 'rxjs/operators';
import {EndUserTerminal} from '@app/services/end-user-terminal';
import {environment} from '@environment';

type ContactID = number;

@Injectable({
  providedIn: 'root'
})
export class GtDirectoryService {
  static readonly WEB_RTC_CLIENT_TYPE = 1000;

  private contactsStore = new Map<string, ReplaySubject<ContactModel[]>>();
  private contactImagesStore = new Map<ContactID, ContactImage>();
  private serviceNumberStore = new Map<string, ReplaySubject<ServiceNumberModel[]>>();

  constructor(
    private directory: DirectoryService,
    private directoryV2: DirectoryV2Service,
    private authenticationService: GtAuthenticationService,
    private manageOrganizationService: GtManageOrganizationService,
    private absenceService: GtAbsenceService,
    private pushEventService: PushEventService
  ) {
    this.subscribeEUPush();
    this.subscribeAbsencePush();
    this.subscribeAgentStatePush();
  }

  private subscribeEUPush() {
    const ps = this.pushEventService.pushSubjects;
    merge(ps.SkillChanged, ps.SkillRemoved).subscribe((event: any) => {
      this.handleSkillEvent(event);
    });
    merge(ps.EUCreateCall, ps.EUDisconnectedCall, ps.EUAnsweredCall, ps.EUExpiredCall, ps.EUNoConnectCall)
      .pipe(
        filter(event => event.trigger_point !== 0 || environment.allowEUEventsFromQueue),
        tap(event => this.updateEUState(event.oname, event.full_number, this.getStateFromEvent(event)))
      )
      .subscribe();
    ps.SkillAdded.subscribe(event => {
      this.fetchServiceNumberData(event.oname).subscribe();
    });
  }

  private getStateFromEvent(event: EUCallEvent): number {
    if (event.new_state !== null && event.new_state !== undefined) {
      return +event.new_state;
    } else {
      switch (event.pushEventType) {
        case 'EUCreateCall':
          return 2;
        case 'EUAnsweredCall':
          return 3;
        default:
          return 0;
      }
    }
  }

  private subscribeAbsencePush() {
    const ps = this.pushEventService.pushSubjects;
    ps.Absent.subscribe(event => {
      this.updateAbsenceId(event.oname, event.login, event.id);
    });
    ps.Available.subscribe(event => {
      this.updateAbsenceId(event.oname, event.login, 0);
    });
  }

  private subscribeAgentStatePush() {
    this.pushEventService.pushSubjects.AgentStateTransition.subscribe(event => {
      this.handleAgentStateTransition(event);
    });
  }

  private updateAbsenceId(oname: string, login: string, absenceId: number) {
    this.getContacts(oname, false).subscribe(contacts => {
      if (contacts) {
        const relatedContact = contacts.find(contact => login === contact.login);

        if (relatedContact) {
          this.absenceService.getAbsenceEntryById(absenceId, oname).subscribe(absenceEntry => {
            relatedContact.absenceEntry = absenceEntry;
            this.setContactStatusText(relatedContact);
            this.contactsStore.get(oname).next(contacts);
          });
        }
      }
    });
  }

  private handleAgentStateTransition(event: AgentStateTransition) {
    this.getServiceNumberModels(event.oname, false).subscribe(list => {
      list.forEach(s => {
        const handled = s.handleAgentStateTransition(event);
        if (handled) {
          this.serviceNumberStore.get(event.oname).next(list);
        }
      });
    });
  }

  private handleSkillEvent(event: SkillEvent) {
    this.getServiceNumberModels(event.oname, false).subscribe(list => {
      const serviceNumberModel: ServiceNumberModel = list.find(s => s.phoneNumber === event.service_number);
      if (serviceNumberModel) {
        serviceNumberModel.handleSkillEvent(event);
        this.serviceNumberStore.get(event.oname).next(list);
      }
    });
  }

  private updateEUState(oname: string, fullNumber: string, newState: number) {
    this.getContacts(oname, false).subscribe(contacts => {
      const relatedContact: ContactModel = contacts.find(contact => contact.login !== null && contact.phoneNumber === fullNumber);
      if (relatedContact) {
        relatedContact.memberState = newState;
        this.setContactStatusText(relatedContact);
        this.contactsStore.get(oname).next(contacts);
      }
    });
  }

  getServiceNumberModels(oname: string, subscribeChanges: boolean): Observable<ServiceNumberModel[]> {
    if (!this.serviceNumberStore.has(oname)) {
      this.serviceNumberStore.set(oname, new ReplaySubject<ServiceNumberModel[]>(1));
      this.fetchServiceNumberData(oname).subscribe();
    }
    if (subscribeChanges) {
      return this.serviceNumberStore.get(oname);
    } else {
      return this.serviceNumberStore.get(oname).pipe(first());
    }
  }

  getManagedServiceNumbers(): Observable<ServiceNumberModel[]> {
    return this.manageOrganizationService.organization$.pipe(
      map(o => o.id),
      distinctUntilChanged(),
      switchMap(id => this.getServiceNumberModels(id, true))
    );
  }

  fetchServiceNumberData(oname: string): Observable<ServiceNumberModel[]> {
    return forkJoin([this.directoryV2.getServiceNumberData1(oname), this.getFavourites(oname, this.authenticationService.login)]).pipe(
      map(r => {
        const favorites = r[1] || [];
        if (r[0].code === 200 && r[0].serviceNumberData) {
          const serviceNumberDataViewModels = r[0].serviceNumberData.map(ServiceNumberModel.convertServiceNumberData);

          serviceNumberDataViewModels.forEach(serviceNumber => {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            serviceNumber.favourite = favorites.some(favEntry => favEntry['serviceNumber'] === serviceNumber.phoneNumber);
          });

          if (this.serviceNumberStore.has(oname)) {
            this.serviceNumberStore.get(oname).next(serviceNumberDataViewModels);
          }

          return serviceNumberDataViewModels;
        }
        return [];
      })
    );
  }

  getServiceNumbers(organization?: string, login?: string): Observable<Array<ServiceNumber>> {
    return this.directory.getServiceNumbers1(organization, login).pipe(map(response => response.serviceNumbers));
  }

  getServiceNumber(sno: string, organization?: string, login?: string): Observable<ServiceNumber> {
    // TODO Use to getServiceNumber() when api is ready
    return this.getServiceNumbers(organization, login).pipe(map(serviceNumbers => serviceNumbers.find(sn => sn.number === sno)));
  }

  getSipTerminal(oname: string, login: string): Observable<SipTerminal> {
    return new Observable(observer =>
      this.directory.getSipTerminals1(oname, login).subscribe(
        r => {
          if (r.code !== 200) {
            observer.error(r.message);
          }

          const s = r.sipTerminals.find(t => t.clientType === GtDirectoryService.WEB_RTC_CLIENT_TYPE);
          if (!s) {
            observer.error('No suitable terminal found.');
          } else {
            observer.next(s);
            observer.complete();
          }
        },
        e => observer.error(e)
      )
    );
  }

  getContacts(oname: string, subscribe: boolean): Observable<ContactModel[]> {
    if (!this.contactsStore.has(oname)) {
      this.contactsStore.set(oname, new ReplaySubject<ContactModel[]>(1));
      this.fetchContacts(oname).subscribe();
    }
    if (subscribe) {
      return this.contactsStore.get(oname);
    } else {
      return this.contactsStore.get(oname).pipe(first());
    }
  }

  fetchContacts(oname: string): Observable<ContactModel[]> {
    return forkJoin([
      this.directory.getContacts1(oname, null, null, null),
      this.fetchOrganization(oname),
      this.getFavourites(oname, this.authenticationService.login),
      this.absenceService.getAbsences(oname, false)
    ]).pipe(
      map(responses => {
        if (responses[0].code !== 200) {
          throw new Error(responses[0].message);
        }
        const contacts = responses[0].contactList.map(c => ContactModel.convertContactModel(c));
        const departments = this.getDepartmentsFlatStructure(responses[1].departments);
        const favorites = responses[2] || [];
        const absences = responses[3] || [];

        contacts.forEach(contact => {
          const department = departments.find(dep => dep.id === contact.departmentId);
          contact.department = department ? department.name : '';
          if (contact.absenceId) {
            const currentAbsence = absences.find(absence => absence.id === contact.absenceId);
            if (currentAbsence) {
              contact.absenceEntry = currentAbsence;
            }
          }
          this.setContactStatusText(contact);

          // Check if favorite
          // eslint-disable-next-line @typescript-eslint/dot-notation
          contact.favourite = favorites.some(favEntry => favEntry['id'] === contact.id);
        });
        if (this.contactsStore.get(oname)) {
          this.contactsStore.get(oname).next(contacts);
        }
        return contacts;
      }),
      catchError(err => {
        console.error(err);
        return [];
      })
    );
  }

  getDepartmentsFlatStructure(departments: Department[]): Department[] {
    let flatStructureDeps: Department[] = [];
    if (departments) {
      departments.forEach(dep => {
        flatStructureDeps.push(dep);
        flatStructureDeps = flatStructureDeps.concat(this.getDepartmentsFlatStructure(dep.subDepartments));
      });
    }
    return flatStructureDeps;
  }

  getContactImage(contactId: number): Observable<ContactImage> {
    if (!this.contactImagesStore.has(contactId)) {
      return this.fetchContactImage(contactId);
    }
    return of(this.contactImagesStore.get(contactId));
  }

  fetchContactImage(contactId: number): Observable<ContactImage> {
    return this.directory.getContactImage1(undefined, undefined, contactId).pipe(
      checkResponse(),
      map(r => {
        if (r.contactImage && r.contactImage.base64image) {
          this.contactImagesStore.set(contactId, r.contactImage);
          return r.contactImage;
        } else {
          throw new Error('No image');
        }
      })
    );
  }

  createEndUser(endUser: EndUser): Observable<ContactModel> {
    return this.directory.createEndUser1(endUser).pipe(
      checkResponse(),
      switchMap(() => this.fetchContacts(endUser.organization)),
      map(contacts => contacts.find(c => c.login === endUser.login))
    );
  }

  deleteEndUser(oname: string, login: string): Observable<void> {
    return this.directory.deleteEndUser1(oname, login).pipe(
      checkResponse(),
      switchMap(() => this.getContacts(oname, false)),
      tap(contacts => this.contactsStore.get(oname).next(contacts.filter(c => c.login !== login))),
      mapTo(void 0)
    );
  }

  getEndUser(oname: string, login: string): Observable<ContactModel> {
    return this.getContacts(oname, false).pipe(map(contacts => contacts.find(contact => login === contact.login)));
  }

  fetchOrganization(organization: string): Observable<Organization> {
    return this.directory.getOrganization1(organization).pipe(map(response => response.organization));
  }

  editDepartment(organization: string, department: Department): Observable<void> {
    return this.directory.editDepartment1(organization, department).pipe(checkResponse(), mapTo(void 0));
  }

  createDepartment(organization: string, department: Department): Observable<Department> {
    return this.directory.createDepartment1(organization, department).pipe(
      checkResponse(),
      map(response => response.department)
    );
  }

  removeDepartment(organization: string, deptId: number): Observable<void> {
    return this.directory.removeDepartmentWithId1(deptId, organization).pipe(checkResponse(), mapTo(void 0));
  }

  createContact(oname: string, login: string, contact: Contact): Observable<Contact> {
    return new Observable(observable =>
      this.directory.createContact1(oname, login, contact).subscribe(
        r => {
          if (r.code === 200) {
            const updatedContact = ContactModel.convertContactModel(r.contact); // Get ID from backend
            this.getContacts(oname, false).subscribe(l => {
              l.push(updatedContact);
              contact = updatedContact.backendModel as Contact;
              this.contactsStore.get(oname).next(l);
              observable.next(contact);
              observable.complete();
            });
          } else {
            observable.error(r);
          }
        },
        e => {
          observable.error(e);
        }
      )
    );
  }

  deleteContact(oname: string, login: string, contact: Contact): Observable<void> {
    return this.directory.removeContactWithId1(contact.id, oname, login).pipe(
      checkResponse(),
      switchMap(() => this.getContacts(oname, false)),
      tap(contacts => this.contactsStore.get(oname).next(contacts.filter(c => c.id !== contact.id))),
      mapTo(void 0)
    );
  }

  editContact(oname: string, login: string, contact: Contact): Observable<void> {
    return this.directory.editContact1(oname, login, contact).pipe(
      checkResponse(),
      switchMap(_ => this.getContacts(oname, false)),
      tap(contacts => {
        const relatedContact: ContactModel = contacts.find(c => c.id === contact.id);
        if (relatedContact) {
          const index = contacts.indexOf(relatedContact);
          const contactModel = ContactModel.convertContactModel(contact);
          contactModel.absenceEntry = relatedContact.absenceEntry;
          contactModel.department = relatedContact.department;
          contactModel.favourite = relatedContact.favourite;
          this.setContactStatusText(contactModel);
          contacts[index] = contactModel;
        }
        this.contactsStore.get(oname).next(contacts);
      }),
      mapTo(void 0)
    );
  }

  changePassword(oname: string, login: string, password: string): Observable<void> {
    return this.directory.changeUserPassword1(oname, login, password).pipe(checkResponse(), mapTo(void 0));
  }

  grantAdministratorPermission(oname: string, endUserLogin: string): Observable<void> {
    return this.getContacts(oname, false).pipe(
      switchMap(contacts => {
        const endUser = contacts.find(c => (c as EndUser).login === endUserLogin);
        if (!endUser || endUser.administrator) {
          return of(void 0);
        }
        return this.directory.grantAdministratorPermission1(oname, endUserLogin).pipe(
          checkResponse(),
          tap(_ => {
            endUser.administrator = true;
            this.contactsStore.get(oname).next(contacts);
          }),
          mapTo(void 0)
        );
      })
    );
  }

  revokeAdministratorPermission(oname: string, endUserLogin: string): Observable<void> {
    return this.getContacts(oname, false).pipe(
      switchMap(contacts => {
        const endUser = contacts.find(c => (c as EndUser).login === endUserLogin);
        if (!endUser || !endUser.administrator) {
          return of(void 0);
        }
        return this.directory.revokeAdministratorPermission1(oname, endUserLogin).pipe(
          checkResponse(),
          tap(_ => {
            endUser.administrator = false;
            this.contactsStore.get(oname).next(contacts);
          }),
          mapTo(void 0)
        );
      })
    );
  }

  getContactByFullNumber(oname: string, phoneNumber: string): Observable<ContactModel> {
    return this.getContacts(oname, false).pipe(
      map(contacts => contacts.find(c => c.phoneNumber === phoneNumber)),
      tap(contact => {
        if (!contact) {
          throw new Error('Contact not found');
        }
      })
    );
  }

  getEndUserNumbers(oname: string, login: string): Observable<EndUserNumber[]> {
    return new Observable(obs => {
      this.directory.getEndUserNumbers1(oname, login).subscribe(r => {
        if (r.code !== 200) {
          obs.error(r);
          return;
        }
        obs.next(r.endUserNumbers);
        obs.complete();
      });
    });
  }

  editEndUserNumber(oname: string, login: string, endUserNumber: EndUserNumber): Observable<void> {
    return this.directory.editEndUserNumber1(oname, login, endUserNumber).pipe(
      checkResponse(),
      map(() => {})
    );
  }

  getContactByShortNumber(oname: string, shortNumber: string): Observable<ContactModel> {
    return this.getContacts(oname, false).pipe(
      map(contactList => {
        if (contactList && shortNumber && shortNumber.length) {
          return contactList.find(c => c.shortNumber === shortNumber);
        } else {
          return null;
        }
      })
    );
  }

  getFavourites(oname: string, login: string): Observable<Favourite[]> {
    return this.directory.getFavourites1(oname, login).pipe(
      checkResponse(),
      map(r => r.favourites)
    );
  }

  addFavourite(oname: string, login: string, favourite: Favourite): Observable<void> {
    return this.directory.addFavourite1(oname, login, favourite).pipe(
      checkResponse(),
      map(() => {})
    );
  }

  removeFavourite(oname: string, login: string, favourite: Favourite): Observable<void> {
    if (favourite['@type'] === 'ComponentFavourite') {
      const componentFavourite = favourite as ComponentFavourite;
      return this.directory
        .removeComponentFavourite1(componentFavourite.serviceNumber, componentFavourite.cid, oname, login)
        .pipe(checkResponse(), mapTo(void 0));
    }

    const contactFavourite = favourite as ContactFavourite;
    return this.directory
      .removeContactFavourite1(contactFavourite.organization, contactFavourite.id, oname, login)
      .pipe(checkResponse(), mapTo(void 0));
  }

  getEndUserAccess(oname?: string, login?: string): Observable<EndUserAccess[]> {
    return this.directory.getEndUserAccess1(oname, login).pipe(
      checkResponse(),
      map(r => r.endUserAccessList)
    );
  }

  getOrganizationDepartments(oname: string): Observable<Department[]> {
    return new Observable(observable =>
      this.directory.getOrganization1(oname).subscribe(
        r => {
          if (r.code === 200) {
            observable.next(r.organization.departments);
            observable.complete();
          } else {
            observable.error(r.message);
          }
        },
        e => observable.error(e)
      )
    );
  }

  private setContactStatusText(contactModel: ContactModel): void {
    if (!contactModel.isEndUser()) {
      return;
    }
    let euStatus = EUStates[contactModel.memberState];
    if (euStatus === undefined) {
      euStatus = EUStates[0];
    }

    contactModel.status = euStatus.statusText;
    contactModel.statusColor = euStatus.statusColor;
    contactModel.statusIcon = euStatus.statusIcon;
    contactModel.statusImage = null;
    contactModel.imageId = 0;

    if (contactModel.memberState < 1 && contactModel.absenceEntry) {
      this.absenceService.getAbsenceReasons(contactModel.oname, this.authenticationService.login).subscribe(absenceReasons => {
        const reason = absenceReasons.find(r => contactModel.absenceEntry.reasonId === r.id);
        contactModel.status = reason.value;
        contactModel.statusImage = reason.imageData;
        contactModel.imageId = reason.imageId;
        contactModel.statusColor = StatusColor.STATUS_BLACK;
        // + ` ${this.translateService.instant('global.until')} ` + this.endTimeFormatter.transform(absenceEntry.endTime);
      });
    }
  }

  updateQueueStats(oname: string, serviceNumber: string, cid: number, calls: number, firstCreationTime: Date) {
    this.getServiceNumberModels(oname, false).subscribe(list => {
      const serviceNumberModel = list.find(sno => sno.phoneNumber === serviceNumber);
      if (serviceNumberModel) {
        const queueData = serviceNumberModel.queueData.find(qd => qd.cid === cid);
        if (queueData) {
          queueData.calls = calls;
          queueData.firstCreationTime = firstCreationTime;
          this.serviceNumberStore.get(oname).next(list);
        }
      }
    });
  }

  getTrunks(): Observable<Map<string, Array<Trunk>>> {
    return this.directoryV2.getTrunks1().pipe(
      checkResponse(),
      map(response => {
        const trunkMap = new Map<string, Array<Trunk>>();
        for (const k of Object.keys(response.trunks)) {
          trunkMap.set(k, response.trunks[k]);
        }
        console.log('Returning trunkMap', trunkMap?.size, trunkMap);
        return trunkMap;
      })
    );
  }

  getEndUserTerminals(organization: string, login: string): Observable<Array<EndUserTerminal>> {
    return this.directoryV2.getTerminals1(organization, login).pipe(
      checkResponse(),
      // override Terminal with EndUserTerminal as code generation failed us
      map(response => response.terminalMap[login].map(t => t as EndUserTerminal))
    );
  }

  addEndUserTerminal(organization: string, login: string, terminal: EndUserTerminal): Observable<EndUserTerminal> {
    return this.directoryV2.addTerminal1(organization, login, terminal).pipe(
      checkResponse(),
      map(response => response.terminal as EndUserTerminal)
    );
  }

  editEndUserTerminal(organization: string, login: string, terminal: EndUserTerminal): Observable<unknown> {
    return this.directoryV2.editTerminal1(organization, login, terminal).pipe(checkResponse());
  }

  removeEndUserTerminal(organization: string, login: string, fullNumber: string): Observable<unknown> {
    return this.directoryV2.removeTerminal1(fullNumber, organization, login).pipe(checkResponse());
  }
}
